import { Component, OnInit, Inject, OnDestroy, ViewChildren, QueryList } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatAutocomplete } from '@angular/material/autocomplete';
import { MatTableDataSource } from '@angular/material/table';
import { Patient } from '../../patient-management/models/patient.interface';
import { FormGroup, FormControl, Validators, ValidatorFn, ValidationErrors } from '@angular/forms';
import { ControlledSubstanceLogsService } from '../../configuration-management/services/controlled-substance-logs.service';
import { LineItem } from '../../line-item-entry/models/line-item.interface';
import { Client } from 'src/app/client-management/models/client.interface';
import { UserOption } from 'src/app/configuration-management/models/user.interface';
import { RequestService } from 'src/app/requests-results/services/request.service';
import { AccountService } from 'src/app/configuration-management/services/account.service';
import { Branch } from '../../configuration-management/models/branch.interface';
import { LocationService } from '../../configuration-management/services/location.service';
import { LocationSummary } from '../../configuration-management/models/locations.interface';
import { Subscription, Observable, Subject, merge } from 'rxjs';
import { map, startWith, debounceTime, switchMap, distinctUntilChanged } from 'rxjs/operators';
import { LocationMismatchConfirmationDialog, LocationMismatchConfirmationData, LocationMismatchConfirmationResult } from './location-mismatch-confirmation.dialog';
import { BottlesService, ControlledSubstanceBottle, BottlesFilterRequest } from 'src/app/inventory/services/bottles.service';
import {
  ControlledSubstanceLogDialogResult,
  ControlledSubstanceLogV2BindingModel
} from '../../configuration-management/models/controlled-substance-logs.models';

export interface ControlledSubstanceLogDialogData {
    lineItem: LineItem;
    patient: Patient;
    client: Client;
    cslData?: any;
}

interface ControlledSubstanceBottleEntry {
    id: string;
    bottle: ControlledSubstanceBottle;
    isCollapsed: boolean;
    controlledSubstanceLogForm: FormGroup;
    editing?: boolean;
    originalBalance?: number;
    isFinished?: boolean;
    isMoveLocation?: boolean;
}

@Component({
    selector: 'app-controlled-substance-log-dialog',
    templateUrl: './controlled-substance-log.dialog.html',
    styleUrls: ['./controlled-substance-log.dialog.scss']
})
export class ControlledSubstanceLogDialogComponent implements OnInit, OnDestroy {
    @ViewChildren('autoComplete') autoCompletes: QueryList<MatAutocomplete>;
    selectedClient = new Client();
    selectedItem = new LineItem();
    selectedPatient = new Patient();
    selectedBranch = new Branch();
    users: UserOption[];
    locations: LocationSummary[];  // Keep for locationIdToName display method

    // Doctor search properties
    doctorSearchControl = new FormControl('', Validators.required);
    filteredDoctors: Observable<UserOption[]>;

    // Global bottle search properties
    globalBottleSearchControl = new FormControl();
    globalFilteredBottles: Observable<ControlledSubstanceBottle[]>;
    selectedBottle: ControlledSubstanceBottle | null = null;
    private locationChangeSubject = new Subject<void>();

    controlledSubstanceLogForm = new FormGroup({
        requestedForUser: new FormControl(null, Validators.required),
        date: new FormControl(new Date(), Validators.required),
        locationId: new FormControl('', Validators.required),
        bottleEntriesValid: new FormControl('', [this.bottleEntriesValidator()]),
    });

    // For multi-bottle support
    bottleEntries: ControlledSubstanceBottleEntry[] = [];
    bottleEntriesDataSource = new MatTableDataSource<ControlledSubstanceBottleEntry>();

    // Table columns for the bottles table
    bottleDisplayedColumns: string[] = ['bottleId', 'medication', 'amountAdministered', 'wastage', 'balanceOnHand', 'location', 'newLocation', 'notes', 'isFinished', 'isMoveLocation', 'actions'];

    subscriptions: Subscription = new Subscription();
    bottleEntrySubscriptions: Subscription[] = [];

    constructor(
        public dialogRef: MatDialogRef<ControlledSubstanceLogDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ControlledSubstanceLogDialogData,
        public snackBar: MatSnackBar,
        public controlledSubstanceLogsService: ControlledSubstanceLogsService,
        public bottlesService: BottlesService,
        public requestService: RequestService,
        public accountService: AccountService,
        public locationService: LocationService,
        private dialog: MatDialog,
    ) {}

    // Custom validator to check if all bottle entries are valid
    private bottleEntriesValidator(): ValidatorFn {
        return (): ValidationErrors | null => {
            // This validator will be called by updateValueAndValidity, but we need to
            // access the current state of bottleEntries, so we use a closure that
            // captures 'this' context
            if (!this.bottleEntries || this.bottleEntries.length === 0) {
                return { invalidBottleEntries: true };
            }
            // Check if all bottle entries have valid forms and bottles selected
            const allEntriesValid = this.bottleEntries.every(entry => {
                return entry.controlledSubstanceLogForm.valid &&
                       entry.bottle !== null;
            });

            return allEntriesValid ? null : { invalidBottleEntries: true };
        };
    }

    // Method to check bottle entries validity and update the control directly
    private checkBottleEntriesValidity(): ValidationErrors | null {
        if (!this.bottleEntries || this.bottleEntries.length === 0) {
            return { invalidBottleEntries: true };
        }

        // Check if all bottle entries have valid forms and bottles selected
        const allEntriesValid = this.bottleEntries.every(entry => {
            return entry.controlledSubstanceLogForm.valid &&
                   entry.bottle !== null;
        });

        return allEntriesValid ? null : { invalidBottleEntries: true };
    }

    ngOnInit() {
        this.subscriptions.add(this.accountService.getDoctorOptions().subscribe(res => {
            this.users = res.filter(u => !u.name.includes("NP NP"));

            if (this.data && this.data.lineItem != null) {
                // Ensure dateOfWork is a Date object
                const dateOfWork = this.data.lineItem.dateOfWork instanceof Date
                    ? this.data.lineItem.dateOfWork
                    : new Date(this.data.lineItem.dateOfWork);

                const lineItemDoctor = new UserOption();
                lineItemDoctor.id = this.data.lineItem.doctor.id;
                lineItemDoctor.name = this.data.lineItem.doctor.name;
                lineItemDoctor.doctorCode = this.data.lineItem.doctorCode;

                this.controlledSubstanceLogForm = new FormGroup({
                    requestedForUser: new FormControl(lineItemDoctor, Validators.required),
                    date: new FormControl(dateOfWork, Validators.required),
                    locationId: new FormControl(this.data.lineItem.location.id, Validators.required),
                    bottleEntriesValid: new FormControl('', [this.bottleEntriesValidator()]),
                });

                this.selectedClient = this.data.client;
                this.selectedPatient = this.data.patient;

                // Set initial doctor in search control
                const selectedDoctor = this.users.find(u => u.id === this.data.lineItem.doctor.id && u.doctorCode === this.data.lineItem.doctorCode);
                if (selectedDoctor) {
                    this.doctorSearchControl.setValue(selectedDoctor);
                }

            } else {
                // Set current user in search control
                const currentUser = this.users.find(u => u.id === this.accountService.currentUserValue.id);
                if (currentUser) {
                    this.doctorSearchControl.setValue(currentUser);
                }
                this.controlledSubstanceLogForm.controls.requestedForUser.setValue(currentUser ? currentUser : null);
            }


            // Initialize doctor search filtering
            this.initializeDoctorSearch();

            this.subscriptions.add(
            this.controlledSubstanceLogForm.controls.locationId.valueChanges.subscribe(() => {
                this.refreshGlobalBottleFilter();
            })
        );
        }));

        // Load locations for display purposes (locationIdToName method)
        this.subscriptions.add(this.locationService.getControlledSubstanceInventoryLocations().subscribe(res => {
            this.locations = res;
        }));

        // Initialize bottle search without loading all bottles upfront
        this.initializeBottleSearch();

        // Load existing  data if available
        if (this.data && this.data.cslData && this.data.cslData.bottleEntries && this.data.cslData.bottleEntries.length > 0) {
            this.loadCSLData();
        }

        // Update the data source to trigger table refresh
        this.refreshTableDataSource();

        // Update validation after loading entries
        this.updateBottleEntriesValidation();
    }

    initializeDoctorSearch() {
        this.filteredDoctors = this.doctorSearchControl.valueChanges.pipe(
            startWith(''),
            map(value => {
                // If value is an object (doctor was selected), use it for filtering
                const name = typeof value === 'string' ? value : value.name || '';
                return name ? this._filterDoctors(name) : this.users.slice();
            })
        );
    }

    private _filterDoctors(name: string): UserOption[] {
        const filterValue = name.toLowerCase();
        return this.users.filter(doctor =>
            doctor.name.toLowerCase().includes(filterValue)
        );
    }

    displayDoctor(doctor: UserOption): string {
        return doctor && doctor.name ? doctor.name : '';
    }

    onDoctorSelected(doctor: UserOption) {
        if (doctor && doctor.id) {
            this.controlledSubstanceLogForm.controls.requestedForUser.setValue(doctor);
        }
    }

    initializeBottleSearch() {
        // Combine search control changes with location changes
        const searchTrigger = merge(
            this.globalBottleSearchControl.valueChanges.pipe(
                startWith(''),
                distinctUntilChanged()
            ),
            this.locationChangeSubject.pipe(
                map(() => this.globalBottleSearchControl.value || '')
            )
        );

        this.globalFilteredBottles = searchTrigger.pipe(
            debounceTime(300),
            map(value => {
                // If value is an object (bottle was selected), return empty array
                if (typeof value === 'object' && value !== null) {
                    return [];
                }
                // Return the search term for the switchMap
                return typeof value === 'string' ? value : '';
            }),
            // Switch to new observable for each search term
            switchMap(searchTerm => {
                // Call backend API with bottleIdSearch and priority location
                const filters: BottlesFilterRequest = {
                    bottleIdSearch: searchTerm as string,
                    priorityLocationId: this.controlledSubstanceLogForm.controls.locationId.value,
                    medicationSupplyId: this.data.lineItem.groupItemItemId || this.data.lineItem.itemId,
                    pageSize: 25
                };

                return this.bottlesService.getBottles(filters).pipe(
                    map(response => this.filterOutSelectedBottles(response.bottles))
                );
            })
        );
    }

    // Method to refresh the global bottle filter
    private refreshGlobalBottleFilter() {
        // Trigger the location change subject to refresh the search
        this.locationChangeSubject.next();
    }

    // Helper method to filter out already selected bottles from results
    private filterOutSelectedBottles(bottles: ControlledSubstanceBottle[]): ControlledSubstanceBottle[] {
        // Get list of already selected bottle IDs
        const selectedBottleIds = this.bottleEntries
            .filter(entry => entry.bottle !== null)
            .map(entry => entry.bottle.id);

        // Filter out bottles that are already selected
        return bottles.filter(bottle => !selectedBottleIds.includes(bottle.id));
    }

    // New method for selecting bottles from the global search
    selectBottleFromGlobalSearch(bottle: ControlledSubstanceBottle) {
        // Check if bottle location differs from log location
        const logLocationId = this.controlledSubstanceLogForm.controls.locationId.value;

        if (logLocationId && bottle.locationId !== logLocationId) {
            // Show confirmation dialog for location mismatch
            this.showLocationMismatchConfirmation(bottle, logLocationId);
        } else {
            // No location mismatch, proceed normally
            this.assignBottleToEntry(bottle, false);
        }
    }

    // Method to show location mismatch confirmation dialog
    private showLocationMismatchConfirmation(bottle: ControlledSubstanceBottle, logLocationId: string) {
        const bottleLocationName = this.locationIdToName(bottle.locationId);
        const logLocationName = this.locationIdToName(logLocationId);

        const dialogRef = this.dialog.open(LocationMismatchConfirmationDialog, {
            width: '400px',
            data: {
                bottleName: `${bottle.bottleId} - ${bottle.lineItemName}`,
                bottleLocation: bottleLocationName,
                logLocation: logLocationName
            } as LocationMismatchConfirmationData
        });

        dialogRef.afterClosed().subscribe((result: LocationMismatchConfirmationResult) => {
            if (result) {
                // User confirmed, assign bottle with location change preference
                this.assignBottleToEntry(bottle, result.changeLocation);
            }
            // If user cancelled, do nothing
        });
    }

    // Method to assign bottle to entry with location change option
    private assignBottleToEntry(bottle: ControlledSubstanceBottle, changeLocation: boolean) {
        // Find the first empty bottle entry or create a new one
        let targetEntry = this.bottleEntries.find(entry => entry.bottle === null);

        if (!targetEntry) {
            // If no empty entry exists, create a new one
            this.addBottleEntry();
            targetEntry = this.bottleEntries[this.bottleEntries.length - 1];
        }

        // Assign the bottle to the entry with rounded balance
        targetEntry.bottle = bottle;
        targetEntry.isMoveLocation = changeLocation;
        targetEntry.controlledSubstanceLogForm.controls.bottleId.setValue(bottle.bottleId);
        targetEntry.controlledSubstanceLogForm.controls.balanceOnHand.setValue(this.roundToDecimalPlaces(bottle.amountRemaining));
        targetEntry.controlledSubstanceLogForm.controls.isMoveLocation.setValue(changeLocation);
        targetEntry.isCollapsed = false;

        // Sort the bottle entries by bottleId
        this.sortBottleEntries();

        // Update the data source to trigger table refresh
        this.refreshTableDataSource();

        // Clear the global search control
        this.globalBottleSearchControl.setValue('', { emitEvent: false });

        // Update validation after bottle selection
        this.updateBottleEntriesValidation();

        // Refresh the global bottle filter to remove this bottle from search results
        this.refreshGlobalBottleFilter();
    }

    displayBottle(bottle: ControlledSubstanceBottle): string {
        return bottle ? `${bottle.bottleId} - ${bottle.lineItemName}` : '';
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
        // Unsubscribe from all bottle entry subscriptions
        this.bottleEntrySubscriptions.forEach(sub => sub.unsubscribe());
    }

    /**
     * Helper function to round numbers to a specific number of decimal places
     * This prevents JavaScript floating-point precision errors
     */
    private roundToDecimalPlaces(value: number, decimalPlaces: number = 4): number {
        const multiplier = Math.pow(10, decimalPlaces);
        return Math.round(value * multiplier) / multiplier;
    }

    calculateBalance(entry: ControlledSubstanceBottleEntry) {
        if (entry.bottle) {
            // Round input values to 2 decimal places to prevent accumulation of floating-point errors
            const adminAmount = this.roundToDecimalPlaces(Math.max(0, Number(entry.controlledSubstanceLogForm.controls.amountAdministered.value) || 0));
            const wastage = this.roundToDecimalPlaces(Math.max(0, Number(entry.controlledSubstanceLogForm.controls.wastage.value) || 0));
            const originalBalance = this.roundToDecimalPlaces(entry.bottle.amountRemaining);

            // Calculate the new balance with proper rounding
            const newBalance = this.roundToDecimalPlaces(originalBalance - (adminAmount + wastage));

            // Only update if it's a valid calculation
            if (!isNaN(newBalance) && newBalance >= 0) {
                entry.controlledSubstanceLogForm.controls.balanceOnHand.setValue(newBalance);
            }

            // Trigger validation update since balance calculation affects form validity
            this.updateBottleEntriesValidation();
        }
    }

    // Validate that numerical inputs don't go below 0
    validateNumericInput(event: any, entry: ControlledSubstanceBottleEntry, field: string): void {
        const value = parseFloat(event.target.value);
        if (value < 0 || isNaN(value)) {
            event.target.value = 0;
            entry.controlledSubstanceLogForm.controls[field].setValue(0);

            // Recalculate balance if this affects the calculation
            if (field === 'amountAdministered' || field === 'wastage') {
                this.calculateBalance(entry);
            }
        }
    }


    submit() {
        // Mark doctor search control as touched to show validation errors
        this.doctorSearchControl.markAsTouched();

        // Mark all bottle entry form fields as touched to show validation errors
        if (this.controlledSubstanceLogForm.invalid || this.controlledSubstanceLogForm.controls.bottleEntriesValid.invalid || this.doctorSearchControl.invalid) {
            this.markAllFieldsAsTouched();
            return;
        }

        // Calculate total amount administered from all bottles for the line item quantity with proper rounding
        const totalAmountAdministered = this.roundToDecimalPlaces(
            this.bottleEntries.reduce((sum, entry) => {
                return sum + this.roundToDecimalPlaces(Number(entry.controlledSubstanceLogForm.controls.amountAdministered.value) || 0);
            }, 0)
        );

        // Create a separate ControlledSubstanceLogV2BindingModel for each bottle with rounded values
        const controlledSubstanceLogsList: ControlledSubstanceLogV2BindingModel[] = this.bottleEntries.map(entry => ({
            id: entry.id,
            bottleId: entry.bottle.bottleId,
            amountAdministered: this.roundToDecimalPlaces(Number(entry.controlledSubstanceLogForm.controls.amountAdministered.value) || 0),
            wastage: this.roundToDecimalPlaces(Number(entry.controlledSubstanceLogForm.controls.wastage.value) || 0),
            balanceOnHand: this.roundToDecimalPlaces(Number(entry.controlledSubstanceLogForm.controls.balanceOnHand.value) || 0),
            originalBalance: entry.bottle.originalBalance,
            locationId: entry.bottle.locationId,
            requestedForUserId: this.controlledSubstanceLogForm.controls.requestedForUser.value.id,
            note: entry.controlledSubstanceLogForm.controls.notes.value || '',
            controlledSubstanceBottleId: entry.bottle.id || '',
            isFinished: entry.controlledSubstanceLogForm.controls.isFinished.value || false,
            isMoveLocation: entry.controlledSubstanceLogForm.controls.isMoveLocation.value || false,
            lineItemName: entry.bottle.lineItemName
        }));

        // Prepare form data for local storage
        const formData = {
            requestedForUser: this.controlledSubstanceLogForm.controls.requestedForUser.value,
            date: this.controlledSubstanceLogForm.controls.date.value,
            locationId: this.controlledSubstanceLogForm.controls.locationId.value
        };

        this.snackBar.open('Controlled substance log data prepared', 'Success', {
            duration: 2000,
        });

        // Return the list of log entries (one per bottle) and the total amount for quantity
        const result: ControlledSubstanceLogDialogResult = {
            controlledSubstanceLogs: controlledSubstanceLogsList,
            totalAmountAdministered: totalAmountAdministered,
            formData
        };
        this.dialogRef.close(result);
        return;
    }

    onNoClick() {
        this.dialogRef.close();
    }

    // Prevent Enter key from submitting the form
    onFormKeydown(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault();
        }
    }

    // Set up subscriptions to monitor changes in bottle entry forms
    private setupBottleEntrySubscriptions(entry: ControlledSubstanceBottleEntry) {
        // Subscribe to form value changes
        const formSubscription = entry.controlledSubstanceLogForm.valueChanges.pipe(
            debounceTime(300) // Debounce to avoid excessive validation calls
        ).subscribe(() => {
            this.updateBottleEntriesValidation();
        });

        // Store subscriptions for cleanup
        this.bottleEntrySubscriptions.push(formSubscription);
    }

    // Add a bottle entry to the list
    addBottleEntry() {
        // Create a new form group for this entry
        const newControlledSubstanceLogForm = new FormGroup({
            wastage: new FormControl(0, [Validators.required, Validators.min(0)]),
            balanceOnHand: new FormControl(0, [Validators.min(0)]),
            bottleId: new FormControl('', Validators.required),
            amountAdministered: new FormControl(0, [Validators.required, Validators.min(0.001)]),
            notes: new FormControl(''),
            isFinished: new FormControl(false),
            isMoveLocation: new FormControl(false),
        });

        // Create the new entry
        const newEntry: ControlledSubstanceBottleEntry = {
            id: null,
            bottle: null, // Start with no bottle selected
            controlledSubstanceLogForm: newControlledSubstanceLogForm,
            isCollapsed: true
        };

        // Add the entry to our list
        this.bottleEntries.push(newEntry);

        // Update the data source to trigger table refresh
        this.refreshTableDataSource();

        // Set up subscriptions to monitor changes in this entry
        this.setupBottleEntrySubscriptions(newEntry);

        // Update validation after adding entry
        this.updateBottleEntriesValidation();
    }

    // Toggle individual bottle collapse/expand
    toggleBottleEntry(entryId: string) {
        const entry = this.bottleEntries.find(e => e.id === entryId);
        if (entry) {
            entry.isCollapsed = !entry.isCollapsed;
        }
    }

    // Remove a bottle entry
    removeBottleEntry(entryId: string) {
        const entryIndex = this.bottleEntries.findIndex(entry => entry.id === entryId);
        if (entryIndex !== -1) {
            // Clean up subscriptions for this entry (now only 1 subscription per entry: form)
            const subscriptionsToRemove = this.bottleEntrySubscriptions.splice(entryIndex, 1);
            subscriptionsToRemove.forEach(sub => sub.unsubscribe());

            // Remove the entry from the array
            this.bottleEntries.splice(entryIndex, 1);

            // Update the data source to trigger table refresh
            this.refreshTableDataSource();

            // Update validation after removing entry
            this.updateBottleEntriesValidation();

            // Refresh the global bottle filter to make the removed bottle available again
            this.refreshGlobalBottleFilter();
        }
    }

    // Method to trigger validation update for bottle entries
    private updateBottleEntriesValidation() {
        // Check the current validity of bottle entries
        const validationErrors = this.checkBottleEntriesValidity();

        // Set the errors directly on the control
        this.controlledSubstanceLogForm.controls.bottleEntriesValid.setErrors(validationErrors);

        // Also trigger updateValueAndValidity to ensure the form state is updated
        this.controlledSubstanceLogForm.controls.bottleEntriesValid.updateValueAndValidity();
    }

    locationIdToName(locationId: string): string {
        if (!this.locations) {
            return '';
        }
        if (!this.locations.find(loc => loc.id === locationId)) {
            return '';
        }
        return this.locations.find(loc => loc.id === locationId).name || '';
    }

    // Method to load existing data into the form
    private loadCSLData() {
        if (!this.data.cslData || !this.data.cslData.bottleEntries) {
            return;
        }

        this.data.cslData.bottleEntries.forEach((bottleEntry: any) => {
            // Reconstruct bottle object from saved data with rounded values
            const bottle: ControlledSubstanceBottle = {
                id: bottleEntry.controlledSubstanceBottleId || bottleEntry.id || '',
                bottleId: bottleEntry.bottleId,
                amountRemaining: this.roundToDecimalPlaces(bottleEntry.originalBalance || bottleEntry.balanceOnHand),
                originalBalance: this.roundToDecimalPlaces(bottleEntry.originalBalance === null ? bottleEntry.balanceOnHand : bottleEntry.originalBalance),
                locationId: bottleEntry.locationId,
                location: '',
                lineItemName: bottleEntry.lineItemName || '',
                code: bottleEntry.code || '',
                dateModified: new Date(),
                medicationSupplyId: '',
                isFinished: bottleEntry.isFinished || false,
            };

            // Create a new form group for this entry with rounded values
            const newControlledSubstanceLogForm = new FormGroup({
                wastage: new FormControl(this.roundToDecimalPlaces(bottleEntry.wastage), [Validators.required, Validators.min(0)]),
                balanceOnHand: new FormControl(this.roundToDecimalPlaces(bottleEntry.balanceOnHand), [Validators.min(0)]),
                bottleId: new FormControl(bottleEntry.bottleId, Validators.required),
                amountAdministered: new FormControl(this.roundToDecimalPlaces(bottleEntry.amountAdministered), [Validators.required, Validators.min(0)]),
                notes: new FormControl(bottleEntry.notes || ''),
                locationId: new FormControl(bottleEntry.locationId || ''),
                isFinished: new FormControl(bottleEntry.isFinished || false),
                isMoveLocation: new FormControl(bottleEntry.isMoveLocation || false),
            });

            // Create the entry
            const newEntry: ControlledSubstanceBottleEntry = {
                id: bottleEntry.id,
                bottle: bottle,
                controlledSubstanceLogForm: newControlledSubstanceLogForm,
                isCollapsed: false // Show expanded since it has data
            };

            // Add the entry to our list
            this.bottleEntries.push(newEntry);

            // Set up subscriptions to monitor changes in this entry
            this.setupBottleEntrySubscriptions(newEntry);
        });

        // Sort the loaded entries by bottleId
        this.sortBottleEntries();
    }

    // Method to mark all bottle entry form fields as touched
    private markAllFieldsAsTouched() {
        this.controlledSubstanceLogForm.markAllAsTouched();
        this.bottleEntries.forEach(entry => {
            // Mark all form controls in the bottle entry form as touched
            Object.keys(entry.controlledSubstanceLogForm.controls).forEach(key => {
                entry.controlledSubstanceLogForm.get(key).markAsTouched();
            });
        });

        this.snackBar.open('Please fill in all required fields for each bottle entry.', 'Error', {
            duration: 3000,
            panelClass: ['error-snackbar']
        });
    }

    saveEdit(bottleEntry: ControlledSubstanceBottleEntry) {
        // Validate the data and apply rounding to prevent floating-point errors
        const amountAdministeredValue = this.roundToDecimalPlaces(Number(bottleEntry.controlledSubstanceLogForm.get('amountAdministered').value) || 0);
        const wastageValue = this.roundToDecimalPlaces(Number(bottleEntry.controlledSubstanceLogForm.get('wastage').value) || 0);
        const balanceValue = this.roundToDecimalPlaces(Number(bottleEntry.controlledSubstanceLogForm.get('balanceOnHand').value) || 0);

        // Ensure numeric values are not negative and set rounded values
        if (amountAdministeredValue < 0) {
            bottleEntry.controlledSubstanceLogForm.get('amountAdministered').setValue(0);
        } else {
            bottleEntry.controlledSubstanceLogForm.get('amountAdministered').setValue(amountAdministeredValue);
        }

        if (wastageValue < 0) {
            bottleEntry.controlledSubstanceLogForm.get('wastage').setValue(0);
        } else {
            bottleEntry.controlledSubstanceLogForm.get('wastage').setValue(wastageValue);
        }

        if (balanceValue < 0) {
            bottleEntry.controlledSubstanceLogForm.get('balanceOnHand').setValue(0);
        } else {
            bottleEntry.controlledSubstanceLogForm.get('balanceOnHand').setValue(balanceValue);
        }

        // Recalculate balance based on administered amount and wastage
        this.calculateBalance(bottleEntry);

        // Clear editing state
        bottleEntry.editing = false;

        // Update validation after editing
        this.updateBottleEntriesValidation();
    }

    // Handle finish bottle checkbox change
    onIsFinishedChange(bottleEntry: ControlledSubstanceBottleEntry, event: any) {
        bottleEntry.isFinished = event.checked;
    }

    // Method to sort bottle entries by bottleId
    private sortBottleEntries() {
        this.bottleEntries.sort((a, b) => {
            // Put entries without bottles at the end
            if (!a.bottle && !b.bottle) return 0;
            if (!a.bottle) return 1;
            if (!b.bottle) return -1;

            // Sort by bottleId (numeric comparison)
            return a.bottle.bottleId.localeCompare(b.bottle.bottleId);
        });
    }

    // Method to refresh the table data source
    private refreshTableDataSource() {
        this.bottleEntriesDataSource.data = [...this.bottleEntries];
    }

    // Getter to calculate total amount administered with proper rounding
    get totalAmountAdministered(): number {
        return this.roundToDecimalPlaces(
            this.bottleEntries.reduce((total, entry) => {
                const amountAdministeredControl = entry.controlledSubstanceLogForm.get('amountAdministered');
                const amount = amountAdministeredControl ? this.roundToDecimalPlaces(Number(amountAdministeredControl.value) || 0) : 0;
                return total + amount;
            }, 0)
        );
    }

    // Getter to calculate total wastage with proper rounding
    get totalWastage(): number {
        return this.roundToDecimalPlaces(
            this.bottleEntries.reduce((total, entry) => {
                const wastageControl = entry.controlledSubstanceLogForm.get('wastage');
                const wastage = wastageControl ? this.roundToDecimalPlaces(Number(wastageControl.value) || 0) : 0;
                return total + wastage;
            }, 0)
        );
    }
}
